'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ChatSession {
  id: string;
  date: string;
  messageCount: number;
  moodIndicator: 'crisis' | 'regular' | 'guided';
  preview: string;
  timestamp: Date;
}

interface ChatHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionSelect: (sessionId: string) => void;
  currentSessionId?: string;
}

const ChatHistoryDrawer = ({ isOpen, onClose, onSessionSelect, currentSessionId }: ChatHistoryDrawerProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['today']));

  const mockSessions: ChatSession[] = [
    {
      id: '1',
      date: 'Today',
      messageCount: 12,
      moodIndicator: 'regular',
      preview: 'Discussed morning anxiety and breathing exercises...',
      timestamp: new Date('2026-01-14T10:30:00'),
    },
    {
      id: '2',
      date: 'Today',
      messageCount: 8,
      moodIndicator: 'crisis',
      preview: 'Crisis support - panic attack management...',
      timestamp: new Date('2026-01-14T08:15:00'),
    },
    {
      id: '3',
      date: 'Yesterday',
      messageCount: 15,
      moodIndicator: 'guided',
      preview: 'Guided meditation session on letting go...',
      timestamp: new Date('2026-01-13T19:45:00'),
    },
    {
      id: '4',
      date: 'Yesterday',
      messageCount: 20,
      moodIndicator: 'regular',
      preview: 'Work stress and boundary setting strategies...',
      timestamp: new Date('2026-01-13T14:20:00'),
    },
    {
      id: '5',
      date: 'This Week',
      messageCount: 18,
      moodIndicator: 'regular',
      preview: 'Sleep patterns and evening routine discussion...',
      timestamp: new Date('2026-01-11T21:00:00'),
    },
  ];

  const groupedSessions = mockSessions.reduce((acc, session) => {
    const group = session.date.toLowerCase();
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(session);
    return acc;
  }, {} as Record<string, ChatSession[]>);

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const getMoodIcon = (mood: ChatSession['moodIndicator']) => {
    switch (mood) {
      case 'crisis':
        return { name: 'ExclamationTriangleIcon' as const, color: 'text-error' };
      case 'guided':
        return { name: 'SparklesIcon' as const, color: 'text-accent' };
      default:
        return { name: 'ChatBubbleLeftRightIcon' as const, color: 'text-primary' };
    }
  };

  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-background z-800"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={`fixed top-0 left-0 h-full w-[280px] lg:w-[320px] bg-card shadow-warm-lg z-900 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <Icon name="ClockIcon" size={24} className="text-primary" />
              <h2 className="text-xl font-semibold text-foreground font-heading">Chat History</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-md transition-therapeutic"
              aria-label="Close chat history"
            >
              <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {Object.entries(groupedSessions).map(([group, sessions]) => (
              <div key={group} className="space-y-1">
                <button
                  onClick={() => toggleGroup(group)}
                  className="w-full flex items-center justify-between p-3 hover:bg-muted rounded-md transition-therapeutic text-left"
                >
                  <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-caption">
                    {group}
                  </span>
                  <Icon
                    name="ChevronDownIcon"
                    size={16}
                    className={`text-muted-foreground transition-transform duration-250 ${
                      expandedGroups.has(group) ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedGroups.has(group) && (
                  <div className="space-y-1 pl-2">
                    {sessions.map((session) => {
                      const moodIcon = getMoodIcon(session.moodIndicator);
                      const isActive = session.id === currentSessionId;

                      return (
                        <button
                          key={session.id}
                          onClick={() => {
                            onSessionSelect(session.id);
                            onClose();
                          }}
                          className={`w-full p-3 rounded-lg transition-therapeutic text-left ${
                            isActive
                              ? 'bg-primary/10 border border-primary/20' :'hover:bg-muted border border-transparent'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Icon
                              name={moodIcon.name}
                              size={20}
                              className={`${moodIcon.color} flex-shrink-0 mt-0.5`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs text-muted-foreground font-mono">
                                  {formatTime(session.timestamp)}
                                </span>
                                <span className="text-xs text-muted-foreground font-caption">
                                  {session.messageCount} messages
                                </span>
                              </div>
                              <p className="text-sm text-foreground line-clamp-2">
                                {session.preview}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-border">
            <button
              onClick={() => {}}
              className="w-full flex items-center justify-center gap-2 p-3 bg-muted hover:bg-muted/80 rounded-lg transition-therapeutic"
            >
              <Icon name="TrashIcon" size={18} className="text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Clear History</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatHistoryDrawer;